import * as APIRoutes from '@mm/api-routes';
import { buildQueryString, ObjectUtils } from '@mm/utils';
import * as queryString from 'query-string';
import {
  AccessiblePagesByRoles,
  AvailablePage,
  BasicPage,
} from '../interfaces/connected-page';
import { PlanTypeBE } from '../interfaces/subscription';
import ChatbotModel from '../model/ChatbotModel';
import ConnectedPageModel from '../model/ConnectedPageModel';
import { SubscriptionModel } from '../model/SubscriptionModel';
import { Pagination } from '../store-interfaces';
import AuthenticatedController from './AuthenticatedController';
import store from '../store';
import { selectIsShopifyContext } from '../selectors/viewSelectors';

type PostResponse = {
  errorCode?: any;
  errors: any[];
  facebookPage: ConnectedPageModel;
  bot: ChatbotModel;
  success: boolean;
};

type BasicListParams = {
  companyId?: number;
  withSubscription?: boolean;
  withFacebookErrors?: boolean;
  withDowngradePending?: boolean;
  withMessagesStatistic?: boolean;
  withPastSubscriptions?: boolean;
  withContactsCount?: boolean;
  searchQuery?: string;
  withActivations?: boolean;
  showInstagramAccounts?: boolean;
};

type GetAllByCompanyParams = {
  companyId?: number;
  pageNumber?: number;
  perPage?: number;
  withSubscription?: boolean;
  withDowngradePending?: boolean;
  searchQuery?: string;
  withFacebookErrors?: boolean;
  showInstagramAccounts?: boolean;
  showActivated?: boolean;
};

type GetAllByCompanyResponse = {
  facebookPages: ConnectedPageModel[];
  pagination: Pagination;
};

export type SubscriptionMessagingResponse = {
  status: string;
};

export type AccessTokenResponse = {
  errors?: string[];
};

const typeWeights = {
  [PlanTypeBE.pro]: 0,
  [PlanTypeBE.team]: 2,
  [PlanTypeBE.proSpecial]: 4,
  [PlanTypeBE.teamSpecial]: 6,
};

const findPrimarySubscriptionShopify = (subs: SubscriptionModel[]) => {
  console.debug('Subs:', subs);
  const active = subs.find(v => v.state === 'active');
  if (active) {
    return active;
  }

  const trial = subs.find(v => v.state === 'in_trial');
  if (trial) {
    return trial;
  }

  return subs[0];
};

const findPrimarySubscription = (subscriptions: SubscriptionModel[]) => {
  const state = store.getState();
  const isShopify = selectIsShopifyContext(state);

  if (isShopify) {
    const out = findPrimarySubscriptionShopify(subscriptions);
    console.debug('Current Sub:', out);
    return out;
  }

  return subscriptions.sort(sub => {
    const typeWeight = typeWeights[sub.planType];
    const pagesWeight = sub.pagesLimit === null ? 0 : 1;
    return typeWeight + pagesWeight;
  })[0];
};

const mapToBackwardsCompatiblePage = <T extends ConnectedPageModel | BasicPage>(
  facebookPage: T,
): T => {
  if (facebookPage.subscriptions && facebookPage.subscriptions.length) {
    return {
      subscription: findPrimarySubscription(facebookPage.subscriptions),
      ...facebookPage,
    };
  }

  return facebookPage;
};

export default class ConnectedPageController extends AuthenticatedController {
  static async getAll(
    params: {
      companyId?: number;
      botId?: number;
      allCompanies?: boolean;
      light?: boolean;
      showInstagramAccounts?: boolean;
      perPage?: number;
      skipAvailable?: boolean;
    } = {},
  ): Promise<GetAllByCompanyResponse> {
    const { facebookPages, pagination } = await super.get(
      APIRoutes.CONNECTED_PAGES(
        `?${queryString.stringify(ObjectUtils.deepSnakeKeys(params))}`,
      ),
    );
    const mappedFacebookPages = facebookPages.map(page =>
      mapToBackwardsCompatiblePage<ConnectedPageModel>(page),
    );

    return { facebookPages: mappedFacebookPages, pagination };
  }

  static async getAllByCompany(
    params: GetAllByCompanyParams = {},
  ): Promise<GetAllByCompanyResponse> {
    const {
      companyId,
      pageNumber,
      perPage,
      withSubscription,
      withDowngradePending,
      searchQuery,
      withFacebookErrors,
      showInstagramAccounts,
      showActivated,
    } = params;

    // TODO: Make buildQueryString automatically convert to snake case
    const queryString = buildQueryString({
      company_id: companyId,
      per_page: perPage || (withSubscription ? 10 : 20),
      page: pageNumber,
      light: withSubscription,
      with_subscription: withSubscription,
      with_downgrade_pending: withDowngradePending,
      with_facebook_errors: withFacebookErrors,
      with_messages_statistic: true,
      show_instagram_accounts: showInstagramAccounts,
      show_activated: showActivated,
      query: searchQuery,
    });

    const { pagination, facebookPages } = await super.get(
      APIRoutes.CONNECTED_PAGES(queryString),
    );
    const mappedFacebookPages = facebookPages.map(page =>
      mapToBackwardsCompatiblePage<ConnectedPageModel>(page),
    );

    return { facebookPages: mappedFacebookPages, pagination };
  }

  static async getAllPageBasicListByCompany(params: BasicListParams = {}) {
    const {
      companyId,
      withSubscription,
      withFacebookErrors,
      withDowngradePending,
      withMessagesStatistic,
      withPastSubscriptions,
      withContactsCount,
      searchQuery,
      withActivations = true,
      showInstagramAccounts,
    } = params;

    const queryString = buildQueryString({
      company_id: companyId,
      per_page: 500,
      light: true,
      with_downgrade_pending: withDowngradePending || false,
      with_subscription: withSubscription || false,
      with_facebook_errors: withFacebookErrors || false,
      with_messages_statistic: withMessagesStatistic || false,
      with_past_subscriptions: withPastSubscriptions || false,
      with_contacts_count: withContactsCount || false,
      show_activated: withActivations,
      query: searchQuery,
      show_instagram_accounts: showInstagramAccounts,
    });

    const { facebookPages } = await super.get(
      APIRoutes.CONNECTED_PAGES(queryString),
    );
    const mappedFacebookPages = facebookPages.map(page =>
      mapToBackwardsCompatiblePage<BasicPage>(page),
    );

    return { facebookPages: mappedFacebookPages };
  }

  static async allowPageForUsers(params: {
    pageId: number;
    allowedUserIds: number[];
    allowedAgentsIds: number[];
  }) {
    const { pageId, allowedUserIds, allowedAgentsIds } = params;

    const body = ObjectUtils.mapObjKeysToSnakeCase({
      grantedUserIds: allowedUserIds.join(','),
      liveChatUserIds: allowedAgentsIds.join(','),
    });

    return super.put(APIRoutes.ALLOW_PAGE_FOR_USERS(pageId), body);
  }

  static moveToCompany(
    pageId: number,
    companyId: number,
    fromCompanyId: number,
  ) {
    return super.post(APIRoutes.MOVE_PAGE_TO_COMPANY(pageId), {
      companyId,
      fromCompanyId,
    });
  }

  static async refreshAccessToken(
    pageId: number,
  ): Promise<null | AccessTokenResponse> {
    return super.put(
      `${APIRoutes.CONNECTED_PAGE(pageId)}/refresh_access_token`,
    );
  }

  static async retrieveSubscriptionMessagingStatus(
    pageId: number,
  ): Promise<SubscriptionMessagingResponse> {
    return super.get(
      `${APIRoutes.CONNECTED_PAGE(pageId)}/subscription_messaging_status`,
    );
  }

  static async retrievePage(
    pageId: number,
    companyId: number,
    withDowngradePending?: boolean,
    showInstagramAccounts?: boolean,
    withUnlayerHmac?: boolean,
  ): Promise<ConnectedPageModel> {
    const queryString = buildQueryString({
      company_id: companyId,
      with_downgrade_pending: !!withDowngradePending,
      with_messages_statistic: true,
      show_instagram_accounts: !!showInstagramAccounts,
      with_unlayer_hmac: !!withUnlayerHmac,
    });

    const page = await super.get(APIRoutes.CONNECTED_PAGE(pageId, queryString));
    return mapToBackwardsCompatiblePage<ConnectedPageModel>(page);
  }

  static async getAvailablePages(params: {
    limit: number;
    next: string;
    search: string;
    instagram: boolean;
    bypassCache: boolean;
  }) {
    return super.get(
      `${APIRoutes.AVAILABLE_PAGES}?${queryString.stringify(
        ObjectUtils.deepSnakeKeys(params),
      )}`,
    );
  }

  static async getAccessiblePagesByRoles(
    roles: number[],
  ): Promise<AccessiblePagesByRoles> {
    const query = `?${queryString.stringify({ 'roles[]': roles })}`;
    return super.get(`${APIRoutes.ACCESSIBLE_CONNECTED_PAGES}${query}`);
  }

  static postConnectedPage(
    entity: AvailablePage,
    companyId: number | undefined,
    igAccountId: string | undefined,
    onboarding: boolean,
    templateBotId?: number,
    standaloneToken?: string,
    destinationPageId?: number,
  ): Promise<PostResponse> {
    return super.post(APIRoutes.CONNECTED_PAGES(), {
      ...entity,
      igAccountId,
      companyId,
      onboarding,
      templateBotId,
      standaloneToken,
      destinationPageId,
    });
  }

  static async put(entity): Promise<ConnectedPageModel> {
    const page = await super.put(APIRoutes.CONNECTED_PAGE(entity.id), entity);
    return mapToBackwardsCompatiblePage<ConnectedPageModel>(page);
  }

  static deleteConnectedPage(id: number) {
    return super.delete(APIRoutes.CONNECTED_PAGE(id));
  }

  static attachPagesTeam(subscriptionId: number, payload: { ids: number[] }) {
    return super.post(
      APIRoutes.SUBSCRIPTION_ATTACH_PAGES(subscriptionId),
      payload,
    );
  }

  static fetchCustomSquarePicture(
    facebookPageId: number,
  ): Promise<{ squarePicture: string | null }> {
    return super.get(APIRoutes.FB_PAGE_CUSTOM_SQUARE_PICTURE(facebookPageId));
  }
  static updateCustomSquarePicture(
    facebookPageId: number,
    imageId: number,
  ): Promise<{ squarePicture: string | null }> {
    return super.put(APIRoutes.FB_PAGE_CUSTOM_SQUARE_PICTURE(facebookPageId), {
      imageId,
    });
  }
  static removeCustomSquarePicture(facebookPageId: number) {
    return super.delete(
      APIRoutes.FB_PAGE_CUSTOM_SQUARE_PICTURE(facebookPageId),
    );
  }

  static fetchFollowUpConversations(companyId: number) {
    return super.get(APIRoutes.FOLLOW_UP_CONVERSATIONS(companyId));
  }

  static createBusinessPage(
    companyId: number,
    page: Pick<
      ConnectedPageModel,
      'name' | 'businessAssetId' | 'businessWebsite' | 'business'
    >,
  ): Promise<PostResponse> {
    return super.post(APIRoutes.CONNECTED_PAGES(), {
      company_id: companyId,
      ...page,
    });
  }
}
